import { useState, useMemo } from "react";
import { Chart, ChartOptions } from "./Chart/Chart";
import { Flow } from "./Chart/script/FlowRunner";
import { flowEx } from "./flowExample"; 
import { gameEx } from "./gameExample";
import { MockInvest } from "./MockInvest";

const options: ChartOptions = {
  width: "100%",
  height: "782px",
  backgroundColor: '#ffffff',
  padding: "40px",
  border: { color: '#eee', width: "1px", radius: "16px" }
};

export default function App() {
  const [step, setStep] = useState(1);
  const [currentFlow, setCurrentFlow] = useState<Flow[]>(flowEx);
  const [isTutorial, setIsTutorial] = useState(true);

  // 게임 모드의 전체 스텝 수 (gameExample.ts의 마지막 step과 일치)
  const TOTAL_GAME_STEPS = 53;

  const priceInfo = useMemo(() => {
    // 1. 튜토리얼 모드일 때
    if (isTutorial) {
      const tutorialStep = currentFlow.find(f => f.step === step);
      const addData = tutorialStep?.action.find((a: any) => a.type === 'ADD_DATA');
      if (addData && addData.payload.data) {
        const d = addData.payload.data[0];
        return { current: d.closePrice || 0, prev: d.openPrice || 0 };
      }
      return { current: 0, prev: 0 };
    }

    // 2. 실전 게임 모드일 때
    const currentStepObj = currentFlow.find(f => f.step === step);
    const addDataAction = currentStepObj?.action.find((a: any) => a.type === 'ADD_DATA');
    
    if (addDataAction && addDataAction.payload.data) {
      const data = addDataAction.payload.data[0];
      return { 
        current: data.closePrice, 
        prev: data.openPrice 
      };
    }
    
    // Step 1, 2(대기)이거나 데이터를 찾을 수 없는 경우
    // gameExample.ts의 initialData 마지막 값(20번째 데이터)을 기본값으로 설정
    // 시가: 15760, 종가: 15810 (앞서 교정된 시간순 데이터 기준)
    return { current: 15810, prev: 15760 }; 
  }, [step, isTutorial, currentFlow]);

  const handleStepChange = (newStep: number) => {
    // 튜토리얼 종료 -> 게임 시작 전환
    if (isTutorial && newStep > 21) {
      setIsTutorial(false);
      setCurrentFlow(gameEx);
      setStep(1);
      return;
    }

    // 게임 종료 처리
    if (!isTutorial && newStep > TOTAL_GAME_STEPS) {
      // 53턴 이후에는 더 이상 증가시키지 않음
      return;
    }

    if (newStep < 1) return;
    setStep(newStep);
  };

  return (
    <div style={{ padding: '40px', maxWidth: '1600px', margin: '0 auto', fontFamily: 'Pretendard, sans-serif' }}>
      <header style={{ marginBottom: '30px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '28px', color: '#1a1a1a' }}>
            {isTutorial ? "📖 차트 학습 가이드" : "🚀 실전 모의 투자"}
          </h1>
          <p style={{ color: '#666', marginTop: '5px' }}>
            {isTutorial 
              ? `입문 단계: ${step} / 21` 
              : `실전 매매: ${Math.min(step, TOTAL_GAME_STEPS)} / ${TOTAL_GAME_STEPS} 턴`}
          </p>
        </div>
        
        {isTutorial && (
          <button 
            onClick={() => handleStepChange(step + 1)}
            style={{ 
              padding: '12px 30px', 
              cursor: 'pointer', 
              backgroundColor: '#000', 
              color: '#fff', 
              borderRadius: '8px', 
              border: 'none', 
              fontWeight: '600',
              transition: 'background 0.2s'
            }}
            onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#333'}
            onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#000'}
          >
            {step === 21 ? "게임 시작하기" : "다음 단계"}
          </button>
        )}
      </header>

      <div style={{ display: 'flex', gap: '30px', alignItems: 'stretch' }}>
        {/* 차트 영역 */}
        <div style={{ 
          flex: 1, 
          backgroundColor: '#fff', 
          boxShadow: '0 10px 30px rgba(0,0,0,0.05)', 
          borderRadius: '16px', 
          overflow: 'hidden', 
          border: '1px solid #eee' 
        }}>
          <Chart 
            flow={currentFlow} 
            currentStep={step} 
            options={options} 
            onStepChange={handleStepChange} 
          />
        </div>

        {/* 투자 패널 영역 */}
        <MockInvest 
          currentPrice={priceInfo.current} 
          prevPrice={priceInfo.prev}
          isActive={!isTutorial} 
          currentStep={step}
          totalSteps={TOTAL_GAME_STEPS}
          onNextStep={() => handleStepChange(step + 1)}
        />
      </div>
    </div>
  );
}